import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useTransition,
} from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DownloadIcon,
  BuildingIcon,
  CaretUpIcon,
  CaretDownIcon,
  CaretUpDownIcon,
} from '@phosphor-icons/react';
import { formatCurrency } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import Papa from 'papaparse';

import type {
  BreakdownTableProps,
  BreakdownCellContext,
  BreakdownCell,
} from './types';
import { Z_INDEX } from './constants';
import {
  buildTableIndex,
  getCell,
  hasSubDimensions,
  extractNumericValue,
} from './indexing';
import { buildVisibleDimensions } from '@/utils/breakdown/buildVisibleDimensions';
import { decorateDimensions } from '@/utils/breakdown/decorateDimensions';
import {
  aggregateForScope,
  type AggregateForScopeParams,
} from '@/utils/breakdown/aggregationHelpers';
import { DimensionHeader } from './DimensionHeader';
import { BreakdownTableDataRow } from './BreakdownTableDataRow';

const cellValueHasData = (value: BreakdownCell['value']): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (React.isValidElement(value)) {
    return true;
  }

  if (typeof value === 'object') {
    if ('current' in value) {
      const current = value.current ?? 0;
      const previous = value.previous ?? 0;
      const delta = value.delta ?? 0;
      return current !== 0 || previous !== 0 || delta !== 0;
    }

    return Object.values(value).some((entry) => {
      if (typeof entry === 'number') {
        return entry !== 0;
      }
      if (typeof entry === 'string') {
        return entry.trim().length > 0;
      }
      return Boolean(entry);
    });
  }

  return false;
};

// Re-export types
export type {
  BreakdownCell,
  BreakdownTableIndex,
  BreakdownCellContext,
  BreakdownTableProps,
} from './types';

// Reusable class variables for table styling

const headerRowBaseClasses = cn('m-0');

const headerCellBaseClasses = cn('align-middle leading-[1.2] opacity-100');

// Header background color
const HEADER_BG = 'bg-gray-200';

// Shaded column background colors
const SHADED_COLUMN_BG = 'bg-gray-100';
const SHADED_COLUMN_TOTAL_BG = 'bg-gray-200';

const headerCellMutedClasses = cn(
  headerCellBaseClasses,
  `${HEADER_BG} text-foreground`,
);

const headerCellWhiteClasses = cn(headerCellBaseClasses, 'bg-white');

const headerCellWhiteWithBorderClasses = cn(headerCellWhiteClasses);

const BreakdownTable: React.FC<BreakdownTableProps> = ({
  data,
  columnLabel = 'Column',
  subColumnLabel = 'Sub-Column',
  rowLabel = 'Row',
  subRowLabel = 'Sub-Row',
  title = 'Breakdown Table',
  subtitle = '',
  icon,
  showCard = true,
  minHeight,
  maxHeight = 700,
  initialExpandedColumns,
  initialExpandedRows,
  expandAllColumns = false,
  expandAllRows = false,
  showXDim1Total = false,
  showYDim1Total = false,
  renderCell,
  renderLabel,
  aggregateCellValues,
  showColumnReferences = false,
  onExportCSV,
  excludeFromCSV,
  xDimOrder,
  xDim2Order,
  yDimOrder,
  yDim2Order,
  fillMissingSubColumns = false,
  fillMissingSubRows = false,
  hideEmptySubdimensions = false,
  hideVerticalLines = false,
  specialColumns = [],
  shadedFromColumn,
  columnHeaderAlign = 'center',
  dataCellAlign,
  onColumnHeaderClick,
  sortColumn,
  sortDirection,
  noAggregateColumns = [],
}) => {
  // Ref for the scrollable container
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Use transition for non-blocking UI updates
  const [isPending, startTransition] = useTransition();

  // Map alignment prop to Tailwind text alignment classes
  const columnHeaderAlignClass =
    columnHeaderAlign === 'left'
      ? 'text-left'
      : columnHeaderAlign === 'right'
        ? 'text-right'
        : 'text-center';

  // Map data cell alignment prop to Tailwind text alignment classes
  const dataCellAlignClass = dataCellAlign
    ? dataCellAlign === 'left'
      ? 'text-left'
      : dataCellAlign === 'right'
        ? 'text-right'
        : 'text-center'
    : undefined;

  // Build index once on mount
  const index = useMemo(
    () =>
      buildTableIndex(data, {
        columnOrder: xDimOrder,
        subColumnOrder: xDim2Order,
        rowOrder: yDimOrder,
        subRowOrder: yDim2Order,
      }),
    [data, xDimOrder, xDim2Order, yDimOrder, yDim2Order],
  );

  // Initialize expansion state once on mount using lazy initializer
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(() => {
    if (expandAllColumns) {
      return new Set(index.columnValues);
    }
    return initialExpandedColumns || new Set<string>();
  });

  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => {
    if (expandAllRows) {
      return new Set(index.rowValues);
    }
    return initialExpandedRows || new Set<string>();
  });

  const handleToggleColumn = useCallback((column: string) => {
    startTransition(() => {
      setExpandedColumns((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(column)) {
          newSet.delete(column);
        } else {
          newSet.add(column);
        }
        return newSet;
      });
    });
  }, []);

  const handleToggleRow = useCallback((row: string) => {
    startTransition(() => {
      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(row)) {
          newSet.delete(row);
        } else {
          newSet.add(row);
        }
        return newSet;
      });
    });
  }, []);

  const handleExpandAllColumns = useCallback(() => {
    startTransition(() => {
      setExpandedColumns(new Set(index.columnValues));
    });
  }, [index.columnValues]);

  const handleCollapseAllColumns = useCallback(() => {
    startTransition(() => {
      setExpandedColumns(new Set());
    });
  }, []);

  const handleExpandAllRows = useCallback(() => {
    startTransition(() => {
      setExpandedRows(new Set(index.rowValues));
    });
  }, [index.rowValues]);

  const handleCollapseAllRows = useCallback(() => {
    startTransition(() => {
      setExpandedRows(new Set());
    });
  }, []);

  // Check if we have sub-dimensions (memoized for performance)
  const hasSubColumns = useMemo(
    () => index.subColumnValues.some((v) => v !== null),
    [index.subColumnValues],
  );
  const hasSubRows = useMemo(
    () => index.subRowValues.some((v) => v !== null),
    [index.subRowValues],
  );

  // Default cell renderer
  const defaultRenderCell = (
    context: BreakdownCellContext,
  ): React.ReactNode => {
    const { cell } = context;
    const value = cell.value;

    if (value === null || value === undefined) {
      return <span className="text-xs text-muted-foreground">-</span>;
    }

    // Handle strings first (before numeric extraction)
    if (typeof value === 'string') {
      // Don't show empty strings or "0" strings
      if (value.trim() === '' || value === '0') {
        return <span className="text-xs text-muted-foreground">-</span>;
      }
      return <span className="text-xs">{value}</span>;
    }

    // JSX.Element (React.ReactElement)
    if (React.isValidElement(value)) {
      return value;
    }

    // Extract numeric value (handles YOY objects, numbers)
    const numValue = extractNumericValue(value);
    if (numValue !== 0 || typeof value === 'number') {
      return (
        <div className="text-xs font-medium text-black-600">
          {formatCurrency(numValue)}
        </div>
      );
    }

    // Fallback for any other type (shouldn't happen, but TypeScript needs this)
    return <span className="text-xs text-muted-foreground">-</span>;
  };

  const cellRenderer = renderCell || defaultRenderCell;

  // Default label renderer
  const defaultRenderLabel = (params: {
    value: string;
    isColumn?: boolean;
    isSubColumn?: boolean;
    isRow?: boolean;
    isSubRow?: boolean;
  }) => {
    // Column headers are bold, row labels are normal weight
    let fontWeight = 'font-normal';
    if (params.isColumn && !params.isSubColumn) {
      fontWeight = 'font-semibold';
    } else if (params.isRow && !params.isSubRow) {
      fontWeight = 'font-medium';
    }

    return (
      <span className={cn('text-xs break-words', fontWeight)}>
        {params.value}
      </span>
    );
  };

  const labelRenderer = renderLabel || defaultRenderLabel;

  const cellVerticalBorderClasses = cn(
    hideVerticalLines && 'border-l-0 border-r-0',
  );

  const cellNoVerticalBordersClasses = cn(
    hideVerticalLines && 'border-l-0 border-r-0',
  );

  // Helper: compute CSS classes for cell borders based on group boundaries
  const borderClasses = useCallback(
    (
      r: { isFirstInGroup: boolean; isLastInGroup: boolean },
      c: { isFirstInGroup: boolean; isLastInGroup: boolean },
    ) => {
      return cn(
        // r.isFirstInGroup && 'border-t',
        // r.isLastInGroup && 'border-b',
        'border-b border-border',
        // !hideVerticalLines && c.isFirstInGroup && 'border-l',
        // !hideVerticalLines && c.isLastInGroup && 'border-r',
        cellNoVerticalBordersClasses,
      );
    },
    [cellNoVerticalBordersClasses],
  );

  // Helper function to check if a sub-column has any non-zero/non-null values
  const subColumnHasData = useCallback(
    (column: string, subColumn: string): boolean => {
      for (const row of index.rowValues) {
        if (hasSubRows) {
          for (const subRow of index.subRowValues) {
            if (subRow === null) continue;
            const cell = getCell(index, column, subColumn, row, subRow);
            if (cell && cellValueHasData(cell.value)) {
              return true;
            }
          }
        } else {
          const cell = getCell(index, column, subColumn, row, null);
          if (cell && cellValueHasData(cell.value)) {
            return true;
          }
        }
      }
      return false;
    },
    [index, hasSubRows],
  );

  // Helper function to check if a sub-row has any non-zero/non-null values
  const subRowHasData = useCallback(
    (row: string, subRow: string): boolean => {
      for (const column of index.columnValues) {
        if (hasSubColumns) {
          for (const subColumn of index.subColumnValues) {
            if (subColumn === null) continue;
            const cell = getCell(index, column, subColumn, row, subRow);
            if (cell && cellValueHasData(cell.value)) {
              return true;
            }
          }
        } else {
          const cell = getCell(index, column, null, row, subRow);
          if (cell && cellValueHasData(cell.value)) {
            return true;
          }
        }
      }
      return false;
    },
    [index, hasSubColumns],
  );

  // Helper: render a single cell with consistent logic (for total row only)
  const renderCellHelper = useCallback(
    (
      r: { primary: string; sub: string | null },
      c: { primary: string; sub: string | null },
    ): React.ReactNode => {
      // Handle total dimensions
      const scope: Partial<AggregateForScopeParams> = {};
      if (r.primary !== '__TOTAL__') {
        scope.row = r.primary;
        scope.subRow = r.sub;
      }
      if (c.primary !== '__TOTAL__') {
        scope.column = c.primary;
        scope.subColumn = c.sub;
      }

      const value = aggregateForScope(
        {
          index,
          ...scope,
          expandedRows,
          expandedColumns,
          fillMissingSubRows,
          fillMissingSubColumns,
          subRowHasData,
          subColumnHasData,
          xDim2Order,
          yDim2Order,
        } as AggregateForScopeParams,
        aggregateCellValues,
      );

      // Try to get the original cell to preserve metadata
      // For non-total cells, look up the original cell from the index
      const originalCell =
        r.primary !== '__TOTAL__' && c.primary !== '__TOTAL__'
          ? getCell(index, c.primary, c.sub, r.primary, r.sub)
          : undefined;

      const cell: BreakdownCell = {
        column: c.primary,
        subColumn: c.sub,
        row: r.primary,
        subRow: r.sub,
        value,
        // Preserve metadata from original cell if available
        metadata: originalCell?.metadata,
      };

      const context: BreakdownCellContext = {
        cell,
        column: c.primary,
        subColumn: c.sub,
        row: r.primary,
        subRow: r.sub,
        isGroupLevel: r.sub === null || r.primary === '__TOTAL__',
      };

      return cellRenderer(context);
    },
    [
      index,
      expandedRows,
      expandedColumns,
      fillMissingSubRows,
      fillMissingSubColumns,
      subRowHasData,
      subColumnHasData,
      xDim2Order,
      yDim2Order,
      aggregateCellValues,
      cellRenderer,
    ],
  );

  // Helper function to check if a column is special
  const isSpecialColumn = useCallback(
    (column: string): boolean => {
      return specialColumns.includes(column);
    },
    [specialColumns],
  );

  // Helper function to check if a column should be shaded
  const isShadedColumn = useCallback(
    (column: string): boolean => {
      if (!shadedFromColumn) return false;
      const shadedIndex = index.columnValues.indexOf(shadedFromColumn);
      if (shadedIndex === -1) return false;
      const columnIndex = index.columnValues.indexOf(column);
      return columnIndex >= shadedIndex;
    },
    [shadedFromColumn, index.columnValues],
  );

  // Build visible dimensions using unified helper
  const visibleColumns = useMemo(
    () =>
      buildVisibleDimensions({
        axis: 'column',
        index,
        expandedSet: expandedColumns,
        fillMissing: fillMissingSubColumns,
        subDimHasData: subColumnHasData,
        subDimOrder: xDim2Order,
        hideEmptySubdimensions,
      }),
    [
      index,
      expandedColumns,
      fillMissingSubColumns,
      subColumnHasData,
      xDim2Order,
      hideEmptySubdimensions,
    ],
  );

  const visibleRows = useMemo(
    () =>
      buildVisibleDimensions({
        axis: 'row',
        index,
        expandedSet: expandedRows,
        fillMissing: fillMissingSubRows,
        subDimHasData: subRowHasData,
        subDimOrder: yDim2Order,
        hideEmptySubdimensions,
      }),
    [
      index,
      expandedRows,
      fillMissingSubRows,
      subRowHasData,
      yDim2Order,
      hideEmptySubdimensions,
    ],
  );

  // Decorate dimensions with presentation metadata
  const decoratedColumns = useMemo(
    () => decorateDimensions(visibleColumns),
    [visibleColumns],
  );

  const decoratedRows = useMemo(
    () => decorateDimensions(visibleRows),
    [visibleRows],
  );

  // Add synthetic total dimensions if needed
  const decoratedColumnsWithTotal = useMemo(() => {
    if (!showXDim1Total) return decoratedColumns;
    return [
      ...decoratedColumns,
      {
        primary: '__TOTAL__',
        sub: null,
        hasChildren: false,
        expanded: false,
        isFirstInGroup: true,
        isLastInGroup: true,
        isGroupHeader: false,
      },
    ];
  }, [decoratedColumns, showXDim1Total]);

  const decoratedRowsWithTotal = useMemo(() => {
    if (!showYDim1Total) return decoratedRows;
    return [
      ...decoratedRows,
      {
        primary: '__TOTAL__',
        sub: null,
        hasChildren: false,
        expanded: false,
        isFirstInGroup: true,
        isLastInGroup: true,
        isGroupHeader: false,
      },
    ];
  }, [decoratedRows, showYDim1Total]);

  // Get columns that should be counted for aggregation (avoids double-counting)
  const aggregationColumns = useMemo(() => {
    return visibleColumns.filter((col) => {
      const colHasSub = hasSubDimensions(index, 'column', col.primary);
      const colIsExpanded = expandedColumns.has(col.primary);

      if (!colHasSub) {
        return true;
      }

      if (colIsExpanded) {
        return col.sub !== null;
      } else {
        return col.sub === null;
      }
    });
  }, [visibleColumns, index, expandedColumns]);

  // CSV export handler
  const handleExportCSV = () => {
    if (onExportCSV) {
      const filename = title
        ? title.toLowerCase().replace(/\s+/g, '-')
        : 'breakdown-table';
      onExportCSV(filename);
      return;
    }

    // Default CSV export implementation - uses same structure as UI
    try {
      if (data.length === 0) {
        console.warn('No data to export');
        return;
      }

      const csvRows: string[][] = [];

      // Build header rows - matching the new two-row structure
      if (hasSubColumns) {
        // First header row - primary columns
        const headerRow1: string[] = [rowLabel];
        index.columnValues.forEach((colValue) => {
          headerRow1.push(colValue);
        });
        if (showXDim1Total) {
          headerRow1.push('Total');
        }
        csvRows.push(headerRow1);

        // Second header row - sub-columns
        const headerRow2: string[] = [''];
        index.columnValues.forEach((colValue) => {
          const hasChildren = hasSubDimensions(index, 'column', colValue);
          const isExpanded = expandedColumns.has(colValue);

          if (hasChildren && isExpanded) {
            const subCols = visibleColumns.filter(
              (vc) => vc.primary === colValue && vc.sub !== null,
            );
            subCols.forEach((subCol) => {
              headerRow2.push(subCol.sub || '');
            });
          } else {
            headerRow2.push('');
          }
        });
        if (showXDim1Total) {
          headerRow2.push('');
        }
        csvRows.push(headerRow2);
      } else {
        // Single header row if no sub-columns
        const headerRow: string[] = [rowLabel];
        index.columnValues.forEach((colValue) => {
          headerRow.push(colValue);
        });
        if (showXDim1Total) {
          headerRow.push('Total');
        }
        csvRows.push(headerRow);
      }

      // Data rows - use same iteration and aggregation as UI rendering
      decoratedRowsWithTotal.forEach((r) => {
        const row: string[] = [];

        // Row label columns
        if (r.primary === '__TOTAL__') {
          row.push('Total');
        } else {
          if (hasSubRows) {
            const displayLabel =
              r.sub === null
                ? r.primary
                : index.subRowKeyToDisplay.get(r.sub) || r.sub || '';
            row.push(displayLabel);
          } else {
            row.push(r.primary);
          }
        }

        // Data columns - matching the new header structure
        index.columnValues.forEach((colValue) => {
          const hasChildren = hasSubDimensions(index, 'column', colValue);
          const isExpanded = expandedColumns.has(colValue);

          if (hasChildren && isExpanded) {
            // Add cells for each visible sub-column
            const subCols = visibleColumns.filter(
              (vc) => vc.primary === colValue && vc.sub !== null,
            );
            subCols.forEach((subCol) => {
              const scope: Partial<AggregateForScopeParams> = {};
              if (r.primary !== '__TOTAL__') {
                scope.row = r.primary;
                scope.subRow = r.sub;
              }
              scope.column = colValue;
              scope.subColumn = subCol.sub;

              const value = aggregateForScope(
                {
                  index,
                  ...scope,
                  expandedRows,
                  expandedColumns,
                  fillMissingSubRows,
                  fillMissingSubColumns,
                  subRowHasData,
                  subColumnHasData,
                  xDim2Order,
                  yDim2Order,
                } as AggregateForScopeParams,
                aggregateCellValues,
              );

              row.push(extractNumericValue(value).toString());
            });
          } else {
            // Add single cell for collapsed or leaf columns
            const scope: Partial<AggregateForScopeParams> = {};
            if (r.primary !== '__TOTAL__') {
              scope.row = r.primary;
              scope.subRow = r.sub;
            }
            scope.column = colValue;
            scope.subColumn = null;

            const value = aggregateForScope(
              {
                index,
                ...scope,
                expandedRows,
                expandedColumns,
                fillMissingSubRows,
                fillMissingSubColumns,
                subRowHasData,
                subColumnHasData,
                xDim2Order,
                yDim2Order,
              } as AggregateForScopeParams,
              aggregateCellValues,
            );

            row.push(extractNumericValue(value).toString());
          }
        });

        // Total column
        if (showXDim1Total) {
          const scope: Partial<AggregateForScopeParams> = {};
          if (r.primary !== '__TOTAL__') {
            scope.row = r.primary;
            scope.subRow = r.sub;
          }

          const value = aggregateForScope(
            {
              index,
              ...scope,
              expandedRows,
              expandedColumns,
              fillMissingSubRows,
              fillMissingSubColumns,
              subRowHasData,
              subColumnHasData,
              xDim2Order,
              yDim2Order,
            } as AggregateForScopeParams,
            aggregateCellValues,
          );

          row.push(extractNumericValue(value).toString());
        }

        csvRows.push(row);
      });

      // Generate CSV content
      const csvContent = Papa.unparse(csvRows);

      // Create filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const baseFilename = title
        ? title.toLowerCase().replace(/\s+/g, '-')
        : 'breakdown-table';
      const downloadFilename = `${baseFilename}-${timestamp}.csv`;

      // Create and trigger download
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };
  // Ref to first header row to find the parent thead
  const firstHeaderRowRef = useRef<HTMLTableRowElement | null>(null);

  // Check if there's no data to display
  const hasNoData =
    data.length === 0 ||
    index.columnValues.length === 0 ||
    index.rowValues.length === 0 ||
    decoratedRowsWithTotal.length === 0;

  // Render empty state message
  const emptyStateContent = (
    <div className="flex items-center justify-center py-12 px-4">
      <div className="text-sm text-muted-foreground">No data available</div>
    </div>
  );

  const tableContent = hasNoData ? (
    emptyStateContent
  ) : (
    <div
      ref={tableContainerRef}
      className="rounded-lg border overflow-x-auto overflow-y-auto w-full max-w-full min-w-0"
      style={{ maxHeight, minHeight }}
    >
      <Table className="relative border-separate border-spacing-0 w-max min-w-full">
        <TableHeader className="border-b border-border">
          {/* First header row - primary columns */}
          <TableRow
            ref={firstHeaderRowRef}
            className={cn(
              headerRowBaseClasses,
              `${HEADER_BG} text-foreground divide-x divide-border`,
            )}
          >
            {/* Y-axis label column - spans both header rows */}
            <TableCell
              className={cn(
                headerCellWhiteWithBorderClasses,
                'sticky left-0 top-0 z-10 font-semibold text-xs h-10 w-[280px] max-w-[280px] p-2 border-b border-border',
                hideVerticalLines && 'border-r-0',
                cellNoVerticalBordersClasses,
              )}
              style={{
                zIndex: Z_INDEX.topLeft,
              }}
              rowSpan={
                hasSubColumns &&
                index.columnValues.some(
                  (col) =>
                    hasSubDimensions(index, 'column', col) &&
                    expandedColumns.has(col),
                )
                  ? 2
                  : 1
              }
            >
              {rowLabel}
            </TableCell>

            {/* Primary column headers with colspan for expanded columns */}
            {index.columnValues.map((colValue, idx) => {
              const hasChildren = hasSubDimensions(index, 'column', colValue);
              const isExpanded = expandedColumns.has(colValue);

              // Check if adjacent columns are special
              const prevColIsSpecial =
                idx > 0 && isSpecialColumn(index.columnValues[idx - 1]);
              const nextColIsSpecial =
                idx < index.columnValues.length - 1 &&
                isSpecialColumn(index.columnValues[idx + 1]);

              // Calculate colspan
              let colspan = 1;
              if (hasChildren && isExpanded) {
                // Count visible sub-columns for this primary column
                const subCols = visibleColumns.filter(
                  (vc) => vc.primary === colValue && vc.sub !== null,
                );
                colspan = subCols.length || 1;
              }

              return (
                <TableCell
                  key={`primary-${colValue}-${idx}`}
                  className={cn(
                    headerCellMutedClasses,
                    'sticky top-0 font-semibold text-xs h-10 p-2',
                    columnHeaderAlignClass,
                    (!hasSubColumns || expandedColumns.size === 0) &&
                      'border-b',
                    cellVerticalBorderClasses,
                    cellNoVerticalBordersClasses,
                    (hasChildren || onColumnHeaderClick) && 'cursor-pointer',
                    // Special column styling - top border always
                    isSpecialColumn(colValue) && 'border-t-2 border-t-black',
                    // Left border: if special column and (leftmost OR previous column not special)
                    // When expanded, border is on the leftmost edge of the colspan
                    isSpecialColumn(colValue) &&
                      !hideVerticalLines &&
                      (idx === 0 || !prevColIsSpecial) &&
                      'border-l-2 border-l-black',
                    // Right border: if special column and (rightmost OR next column not special)
                    // When expanded, border is on the rightmost edge of the colspan
                    isSpecialColumn(colValue) &&
                      !hideVerticalLines &&
                      (idx === index.columnValues.length - 1 ||
                        !nextColIsSpecial) &&
                      'border-r-2 border-r-black',
                  )}
                  style={{
                    zIndex: Z_INDEX.xAxis,
                  }}
                  colSpan={colspan}
                  onClick={
                    hasChildren
                      ? (e) => {
                          e.stopPropagation();
                          if (e.shiftKey) {
                            // Check if all columns with children are expanded
                            const columnsWithChildren =
                              index.columnValues.filter((col) =>
                                hasSubDimensions(index, 'column', col),
                              );
                            const allExpanded = columnsWithChildren.every(
                              (col) => expandedColumns.has(col),
                            );
                            if (allExpanded) {
                              handleCollapseAllColumns();
                            } else {
                              handleExpandAllColumns();
                            }
                          } else {
                            handleToggleColumn(colValue);
                          }
                        }
                      : onColumnHeaderClick
                        ? () => onColumnHeaderClick(colValue)
                        : undefined
                  }
                >
                  <div className="flex items-center justify-center gap-1">
                    <DimensionHeader
                      label={colValue}
                      expanded={isExpanded}
                      hasChildren={hasChildren}
                      onToggle={
                        hasChildren
                          ? () => handleToggleColumn(colValue)
                          : undefined
                      }
                      renderLabel={labelRenderer}
                      isColumn={true}
                      isSubColumn={false}
                    />
                    {onColumnHeaderClick && !hasChildren && (
                      <span className="ml-1">
                        {sortColumn === colValue ? (
                          sortDirection === 'asc' ? (
                            <CaretUpIcon className="h-4 w-4" />
                          ) : (
                            <CaretDownIcon className="h-4 w-4" />
                          )
                        ) : (
                          <CaretUpDownIcon className="h-3 w-3 text-muted-foreground/40" />
                        )}
                      </span>
                    )}
                  </div>
                </TableCell>
              );
            })}

            {/* Total column */}
            {showXDim1Total && (
              <TableCell
                className={cn(
                  headerCellMutedClasses,
                  'sticky top-0 font-semibold text-xs h-10 p-2 border-b',
                  columnHeaderAlignClass,
                  !hideVerticalLines && 'border-l-2 border-r',
                  cellNoVerticalBordersClasses,
                )}
                style={{
                  zIndex: Z_INDEX.xAxis,
                }}
                rowSpan={
                  hasSubColumns &&
                  index.columnValues.some(
                    (col) =>
                      hasSubDimensions(index, 'column', col) &&
                      expandedColumns.has(col),
                  )
                    ? 2
                    : 1
                }
              >
                Total
              </TableCell>
            )}
          </TableRow>

          {/* Second header row - sub-columns (only render if at least one column is expanded) */}
          {hasSubColumns &&
            index.columnValues.some(
              (col) =>
                hasSubDimensions(index, 'column', col) &&
                expandedColumns.has(col),
            ) && (
              <TableRow
                className={cn(
                  headerRowBaseClasses,
                  `${HEADER_BG} text-foreground divide-x divide-border`,
                )}
              >
                {index.columnValues.map((colValue, colIdx) => {
                  const hasChildren = hasSubDimensions(
                    index,
                    'column',
                    colValue,
                  );
                  const isExpanded = expandedColumns.has(colValue);

                  if (hasChildren && isExpanded) {
                    // Render visible sub-columns
                    const subCols = visibleColumns.filter(
                      (vc) => vc.primary === colValue && vc.sub !== null,
                    );

                    // Check if adjacent columns are special
                    const prevColIsSpecial =
                      colIdx > 0 &&
                      isSpecialColumn(index.columnValues[colIdx - 1]);
                    const nextColIsSpecial =
                      colIdx < index.columnValues.length - 1 &&
                      isSpecialColumn(index.columnValues[colIdx + 1]);

                    return subCols.map((subCol, subIdx) => (
                      <TableCell
                        key={`sub-${colValue}-${subCol.sub}-${subIdx}`}
                        className={cn(
                          headerCellMutedClasses,
                          'sticky top-10 font-medium text-xs h-10 p-2',
                          columnHeaderAlignClass,
                          'border-t border-b',
                          !hideVerticalLines && 'border-r',
                          cellNoVerticalBordersClasses,
                          // Special column styling for sub-columns - only left/right borders
                          // Left border: if first sub-column and (leftmost column OR previous column not special)
                          isSpecialColumn(colValue) &&
                            subIdx === 0 &&
                            !hideVerticalLines &&
                            (colIdx === 0 || !prevColIsSpecial) &&
                            'border-l-2 border-l-black',
                          // Right border: if last sub-column and (rightmost column OR next column not special)
                          isSpecialColumn(colValue) &&
                            subIdx === subCols.length - 1 &&
                            !hideVerticalLines &&
                            (colIdx === index.columnValues.length - 1 ||
                              !nextColIsSpecial) &&
                            'border-r-2 border-r-black',
                        )}
                        style={{
                          zIndex: Z_INDEX.xAxis,
                        }}
                      >
                        {labelRenderer({
                          value: subCol.sub!,
                          isColumn: false,
                          isSubColumn: true,
                        })}
                      </TableCell>
                    ));
                  } else {
                    // Render empty/invisible cell for collapsed or leaf columns
                    // Check if adjacent columns are special for border logic
                    const prevColIsSpecial =
                      colIdx > 0 &&
                      isSpecialColumn(index.columnValues[colIdx - 1]);
                    const nextColIsSpecial =
                      colIdx < index.columnValues.length - 1 &&
                      isSpecialColumn(index.columnValues[colIdx + 1]);

                    return (
                      <TableCell
                        key={`empty-${colValue}`}
                        className={cn(
                          headerCellMutedClasses,
                          'sticky top-10 text-xs h-10 p-2 border-b border-border',
                          columnHeaderAlignClass,
                          cellVerticalBorderClasses,
                          cellNoVerticalBordersClasses,
                          // Special column styling for collapsed columns - left/right borders on empty cell
                          isSpecialColumn(colValue) &&
                            !hideVerticalLines &&
                            (colIdx === 0 || !prevColIsSpecial) &&
                            'border-l-2 border-l-black',
                          isSpecialColumn(colValue) &&
                            !hideVerticalLines &&
                            (colIdx === index.columnValues.length - 1 ||
                              !nextColIsSpecial) &&
                            'border-r-2 border-r-black',
                        )}
                        style={{
                          zIndex: Z_INDEX.xAxis,
                        }}
                      >
                        <span className="opacity-0 select-none text-xs">-</span>
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            )}
        </TableHeader>

        <TableBody>
          {/* Render all rows */}
          {decoratedRowsWithTotal.map((r, rowIdx) => {
            const isLastRow = rowIdx === decoratedRowsWithTotal.length - 1;
            const isLastDataRow = isLastRow && r.primary !== '__TOTAL__';

            // Handle total row
            if (r.primary === '__TOTAL__') {
              return (
                <TableRow
                  key="total-row"
                  className="bg-muted divide-x divide-border sticky bottom-0 min-h-10"
                  style={{
                    zIndex: Z_INDEX.xAxis,
                  }}
                >
                  <TableCell
                    className={cn(
                      'sticky left-0 bottom-0 font-bold w-[280px] max-w-[280px] bg-white opacity-100 break-words',
                      !hideVerticalLines && 'border-r',
                    )}
                    style={{
                      zIndex: Z_INDEX.topLeft,
                    }}
                  >
                    Total
                  </TableCell>

                  {/* Render cells matching the header structure */}
                  {index.columnValues.map((colValue) => {
                    const hasChildren = hasSubDimensions(
                      index,
                      'column',
                      colValue,
                    );
                    const isExpanded = expandedColumns.has(colValue);

                    if (hasChildren && isExpanded) {
                      // Render cells for each visible sub-column
                      const subCols = visibleColumns.filter(
                        (vc) => vc.primary === colValue && vc.sub !== null,
                      );

                      return subCols.map((subCol, subIdx) => (
                        <TableCell
                          key={`total-${colValue}-${subCol.sub}-${subIdx}`}
                          className={cn(
                            'bg-muted font-bold',
                            dataCellAlignClass,
                            // Shaded column background (overrides bg-muted if shaded)
                            isShadedColumn(colValue) && SHADED_COLUMN_TOTAL_BG,
                            cellVerticalBorderClasses,
                            cellNoVerticalBordersClasses,
                            // Special column styling for total row sub-columns - bottom + left/right borders
                            isSpecialColumn(colValue) &&
                              'border-b-2 border-b-black',
                            isSpecialColumn(colValue) &&
                              subIdx === 0 &&
                              !hideVerticalLines &&
                              'border-l-2 border-l-black',
                            isSpecialColumn(colValue) &&
                              subIdx === subCols.length - 1 &&
                              !hideVerticalLines &&
                              'border-r-2 border-r-black',
                          )}
                        >
                          {renderCellHelper(r, {
                            primary: colValue,
                            sub: subCol.sub,
                          })}
                        </TableCell>
                      ));
                    } else {
                      // Render single cell for collapsed or leaf columns
                      return (
                        <TableCell
                          key={`total-${colValue}`}
                          className={cn(
                            'bg-muted font-bold',
                            dataCellAlignClass,
                            // Shaded column background (overrides bg-muted if shaded)
                            isShadedColumn(colValue) && SHADED_COLUMN_TOTAL_BG,
                            cellVerticalBorderClasses,
                            cellNoVerticalBordersClasses,
                            // Special column styling for total row collapsed/leaf columns - bottom + left/right borders
                            isSpecialColumn(colValue) &&
                              'border-b-2 border-b-black',
                            isSpecialColumn(colValue) &&
                              !hideVerticalLines &&
                              'border-l-2 border-l-black border-r-2 border-r-black',
                          )}
                        >
                          {renderCellHelper(r, {
                            primary: colValue,
                            sub: null,
                          })}
                        </TableCell>
                      );
                    }
                  })}

                  {/* Total column */}
                  {showXDim1Total && (
                    <TableCell
                      className={cn(
                        'bg-muted font-bold',
                        dataCellAlignClass,
                        !hideVerticalLines && 'border-l-2 border-r',
                        cellNoVerticalBordersClasses,
                      )}
                    >
                      {renderCellHelper(r, {
                        primary: '__TOTAL__',
                        sub: null,
                      })}
                    </TableCell>
                  )}
                </TableRow>
              );
            }

            return (
              <BreakdownTableDataRow
                key={`${r.primary}-${r.sub}`}
                rowData={r}
                rowIndex={rowIdx}
                isLastRow={isLastRow}
                index={index}
                expandedRows={expandedRows}
                expandedColumns={expandedColumns}
                visibleColumns={visibleColumns}
                decoratedColumns={decoratedColumns}
                fillMissingSubRows={fillMissingSubRows}
                fillMissingSubColumns={fillMissingSubColumns}
                subRowHasData={subRowHasData}
                subColumnHasData={subColumnHasData}
                xDim2Order={xDim2Order}
                yDim2Order={yDim2Order}
                aggregateCellValues={aggregateCellValues}
                renderCell={cellRenderer}
                renderLabel={labelRenderer}
                hideVerticalLines={hideVerticalLines}
                cellNoVerticalBordersClasses={cellNoVerticalBordersClasses}
                borderClasses={borderClasses}
                isSpecialColumn={isSpecialColumn}
                isShadedColumn={isShadedColumn}
                handleToggleRow={handleToggleRow}
                handleCollapseAllRows={handleCollapseAllRows}
                handleExpandAllRows={handleExpandAllRows}
                showXDim1Total={showXDim1Total}
                SHADED_COLUMN_BG={SHADED_COLUMN_BG}
                dataCellAlignClass={dataCellAlignClass}
                noAggregateColumns={noAggregateColumns}
              />
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  // Conditionally wrap with card
  if (showCard) {
    const defaultIcon = <BuildingIcon className="h-4 w-4" />;
    const displayIcon = icon || defaultIcon;

    return (
      <div className="w-full max-w-full min-w-0">
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div className="text-primary">{displayIcon}</div>
                  <CardTitle className="text-lg font-medium">{title}</CardTitle>
                </div>
                {subtitle && (
                  <CardDescription className="ml-6">{subtitle}</CardDescription>
                )}
              </div>
              <CardAction>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  className="h-8 px-2"
                >
                  <DownloadIcon className="h-4 w-4" />
                </Button>
              </CardAction>
            </div>
          </CardHeader>
          <CardContent className="p-0">{tableContent}</CardContent>
        </Card>
      </div>
    );
  }

  return <div className="w-full max-w-full min-w-0">{tableContent}</div>;
};

export default BreakdownTable;
